import axios, { AxiosError, AxiosHeaders } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { apiConfig } from './apiConfig';
import {
  currentSpaPath,
  describePendingStepUp,
  markStepUpStarted,
  parseStepUpChallenge,
  stepUpAuthorizeUrl,
  stepUpOnCooldown,
  writePendingStepUp,
} from './stepUp';
import type { StepUpChallenge } from './stepUp';

/**
 * Tenant-aware API client foundation.
 *
 * The axios instance lives outside React so it can be imported anywhere. Auth
 * rides exclusively on the `httpOnly` session cookie (OAuth2-BFF token-handler
 * model — the browser never holds tokens) and the active tenant is
 * authoritative server-side (BFF session), so the client sends neither an
 * `Authorization` header nor a tenant header. The deprecated
 * `setApiAuthToken`/`setApiTenantId` escape hatches were removed by
 * plan-cleanup-001 (2026-08-25) — both had zero call sites.
 */

/** Error code the BFF returns (403) when the session's active tenant is no longer
 *  accessible to the current user (e.g. their membership was revoked mid-session). */
export const TENANT_ACCESS_DENIED_CODE = 'TENANT_ACCESS_DENIED';

/**
 * Registered by `BffSessionProvider` (outside-React registration — this axios
 * instance lives outside React so it cannot call hooks itself) to react to a
 * {@link TENANT_ACCESS_DENIED_CODE} response: refresh the session and surface a
 * one-time notice. `null` unregisters.
 */
let tenantAccessDeniedHandler: (() => void) | null = null;

export function setTenantAccessDeniedHandler(handler: (() => void) | null): void {
  tenantAccessDeniedHandler = handler;
}

function isTenantAccessDeniedBody(data: unknown): boolean {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as { code?: unknown }).code === TENANT_ACCESS_DENIED_CODE
  );
}

/**
 * Registered by `StepUpReplay` (same outside-React registration idiom as
 * {@link setTenantAccessDeniedHandler}) to report a step-up challenge that arrived while a step-up was
 * *already* in its cooldown window — i.e. the user has just been through Keycloak and the assurance level
 * still is not high enough. That is the one step-up outcome the user cannot recover from by trying again,
 * so it needs saying out loud rather than another silent redirect. `null` unregisters.
 */
let stepUpUnsatisfiedHandler: ((challenge: StepUpChallenge) => void) | null = null;

export function setStepUpUnsatisfiedHandler(
  handler: ((challenge: StepUpChallenge) => void) | null,
): void {
  stepUpUnsatisfiedHandler = handler;
}

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Normalised, transport-agnostic error shape consumed by the UI. */
export interface NormalizedApiError {
  /** HTTP status code, or null for network/client errors. */
  status: number | null;
  /** Machine-readable error code. */
  code: string;
  /** Human-readable message. */
  message: string;
  /** Optional structured details from the backend. */
  details?: unknown;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  // Send the BFF session cookie on cross-subdomain (same-site) API calls.
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Attach the CSRF token (cookie → header) on state-changing requests, plus any
// legacy auth/tenant context that may have been registered.
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const headers = AxiosHeaders.from(config.headers);

  // A FormData body (file upload) must let the browser inject its own
  // 'multipart/form-data; boundary=...' Content-Type. This instance sets a
  // 'Content-Type: application/json' default (below) for every other call; if that
  // header is still present when axios's transformRequest runs, it JSON-stringifies the
  // FormData instead of sending it as a multipart body, and the server rejects it with
  // HttpMediaTypeNotSupportedException (stage incident 2026-06-30, PUT
  // /api/v1/store/settings/media/logo — "Content-Type 'application/json' is not
  // supported"). Clearing it here, before transformRequest sees it, is what lets axios's
  // own FormData handling (resolveConfig.js) take over downstream and set the boundary.
  // A per-call `headers: { 'Content-Type': 'multipart/form-data' }` override (used by
  // some call sites for clarity) is harmless here — this clears it regardless.
  if (config.data instanceof FormData) {
    headers.delete('Content-Type');
  }

  const method = (config.method ?? 'get').toUpperCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      headers.set(CSRF_HEADER, csrf);
    }
  }

  config.headers = headers;
  return config;
});

/**
 * Convert an unknown thrown value into a {@link NormalizedApiError}.
 * Acts as the single normalisation point for API failures.
 */
export function normalizeApiError(error: unknown): NormalizedApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      code?: string;
      message?: string;
      details?: unknown;
    }>;
    const data = axiosError.response?.data;

    return {
      status: axiosError.response?.status ?? null,
      code: data?.code ?? axiosError.code ?? 'UNKNOWN_ERROR',
      message: data?.message ?? axiosError.message ?? 'An unexpected error occurred.',
      details: data?.details,
    };
  }

  if (error instanceof Error) {
    return { status: null, code: 'UNKNOWN_ERROR', message: error.message };
  }

  return {
    status: null,
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred.',
  };
}

// ── Response interceptor ─────────────────────────────────────────────────────
// On any 401 from an authenticated endpoint, the BFF session has expired
// (e.g. the server was restarted or the session idle limit was reached). Redirect
// the browser transparently to Keycloak. Because the Keycloak SSO session is
// typically still valid within the working day, the redirect returns without
// showing a login prompt and the user lands back at the page they were on.
//
// /api/v1/me is excluded: its 401 is the normal "not yet authenticated" probe
// that SessionContext handles to decide whether to show the login view.
// /logout is excluded too: hijacking its 401 would send a user who asked to sign
// OUT back into a sign-IN flow, and would swallow submitLogout's own `.catch`
// fallback (the never-resolving promise below settles neither branch).
const REAUTH_EXEMPT_PATHS = ['/api/v1/me', '/logout'];

/**
 * Exact path matching — deliberately NOT `url.includes(path)`.
 *
 * `/api/v1/me` is a string prefix of `/api/v1/merchant-portal/...` (contentApi's base), so a
 * substring test silently exempted every merchant-portal endpoint from re-auth: their 401s
 * rejected instead of refreshing the session, surfacing as spurious errors on an expired
 * session. Comparing the resolved pathname for equality (or a `/`-delimited descendant) keeps
 * the exemption to the two endpoints it is meant for, whether the call site passed a relative
 * or an absolute URL.
 */
function isReauthExempt(rawUrl: string): boolean {
  let pathname = rawUrl;
  try {
    pathname = new URL(rawUrl, apiConfig.baseURL).pathname;
  } catch {
    // Unparseable (e.g. a relative apiConfig.baseURL) — fall back to the raw value.
  }
  return REAUTH_EXEMPT_PATHS.some(
    (exempt) => pathname === exempt || pathname.startsWith(`${exempt}/`),
  );
}

/**
 * Absolute URL of the BFF's authorize endpoint.
 *
 * MUST be absolute. The SPA (`seller-stage.onebazaar.ru`) and the BFF
 * (`api-seller-stage.onebazaar.ru`) are different hosts in every deployed contour, so a
 * relative `/oauth2/authorization/keycloak` resolves against the SPA origin, where the
 * static-file server's SPA fallback answers with `index.html`. The router then boots on an
 * unmatched path, falls through to `*` → `/not-found` inside `RequireAuth`, whose `/me` is
 * still 401 — so the user is dumped on the portal's own login screen instead of being
 * silently re-authenticated. Building it against `apiConfig.baseURL` is what actually makes
 * the request reach Keycloak. (Same helper shape as `sessionApi.bffUrl`, duplicated rather
 * than imported because `sessionApi` imports this module.)
 */
const REAUTH_REDIRECT = resolveAuthorizeUrl();

/**
 * Resolves the authorize URL at module load, tolerating a non-absolute `VITE_API_BASE_URL`.
 *
 * Every contour configures an absolute base (see `.env.example`), but `readString` does not enforce
 * that, and a bare `new URL(path, '/api')` THROWS — at import time, which would white-screen the
 * entire SPA rather than degrade one feature.
 *
 * The fallback resolves against the browser origin, which is correct for a genuinely same-origin
 * deployment but is ALSO exactly the wrong answer if the base URL was merely malformed (a missing
 * `https://`, say) — that would silently recreate the SPA-origin redirect this whole module exists
 * to prevent. So it is logged loudly: a misconfiguration must be one glance at the console away,
 * not an afternoon of debugging a sign-in that goes nowhere.
 */
function resolveAuthorizeUrl(): string {
  const path = '/oauth2/authorization/keycloak';
  try {
    return new URL(path, apiConfig.baseURL).toString();
  } catch {
    if (typeof window === 'undefined') {
      return path;
    }
    const fallback = new URL(path, window.location.origin).toString();
    console.error(
      `[apiClient] VITE_API_BASE_URL ("${apiConfig.baseURL}") is not an absolute URL, so the ` +
        `Keycloak sign-in redirect falls back to this page's own origin (${fallback}). If the BFF ` +
        `is NOT served from this origin, sign-in will fail — set VITE_API_BASE_URL to the BFF's ` +
        `absolute URL (e.g. https://api-seller-stage.onebazaar.ru).`,
    );
    return fallback;
  }
}
const REAUTH_RETURN_KEY = 'obzr:auth:return_path';
const REAUTH_ATTEMPT_KEY = 'obzr:auth:reauth_at';

/**
 * How long a just-started re-auth suppresses another one. Bounds the loop when an endpoint
 * keeps returning 401 even after a successful Keycloak round-trip (issuer mismatch, a
 * downstream outage misreported as 401, a revoked membership): the second 401 within this
 * window rejects normally so the UI can render an error, instead of navigating away again.
 * Long enough to cover a Keycloak round-trip that requires typing credentials.
 */
const REAUTH_COOLDOWN_MS = 90_000;

/**
 * Single-flight guard for the CURRENT document. A dashboard firing N parallel queries
 * against an expired session produces N 401s; without this, each one calls
 * `window.location.assign` and each starts a NEW authorization request on the BFF. That is
 * actively harmful, not merely redundant: the BFF stores exactly ONE
 * `OAuth2AuthorizationRequest` per HttpSession
 * (`HttpSessionOAuth2AuthorizationRequestRepository`), so every extra start overwrites the
 * previous `state` and the callback for the one the browser actually follows fails with
 * `authorization_request_not_found`.
 */
let reauthInFlight = false;

/**
 * True when a re-auth was started so recently that another one would be a loop.
 *
 * Storage access is guarded: `sessionStorage` throws outright when a browser blocks site data
 * (privacy modes, enterprise policy). Failing open here is deliberate — losing the loop-breaker
 * only costs an extra redirect, whereas throwing would escape this rejection handler and leave
 * the user unable to re-authenticate at all.
 */
function reauthOnCooldown(): boolean {
  try {
    const startedAt = Number(sessionStorage.getItem(REAUTH_ATTEMPT_KEY) ?? '0');
    return (
      Number.isFinite(startedAt) && startedAt > 0 && Date.now() - startedAt < REAUTH_COOLDOWN_MS
    );
  } catch {
    return false;
  }
}

/** Best-effort `sessionStorage` write — see {@link reauthOnCooldown} for why this cannot throw. */
function rememberInSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Storage blocked: the return-path/loop-breaker niceties degrade, re-auth still proceeds.
  }
}

/**
 * Same-origin SPA path, or null. Rejects protocol-relative (`//evil.com`) and absolute URLs
 * so the stored value can never turn `location.replace` into an off-site navigation.
 *
 * Exported for `shared/auth/passkeyEnrolment.ts` (plan-platform-001 step 3.1), which parks a return
 * path for the same `REAUTH_RETURN_KEY` restore this module owns — re-validating here rather than
 * duplicating the rule keeps both round trips (step-up and passkey enrolment) safe the same way.
 */
export function safeReturnPath(path: string): string | null {
  if (!path.startsWith('/') || path.startsWith('//')) {
    return null;
  }
  if (path === '/' || path.startsWith('/welcome')) {
    return null; // nothing worth restoring
  }
  return path;
}

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      if (!isReauthExempt(url)) {
        if (reauthInFlight) {
          // A sibling 401 already started the navigation. Starting a second one would
          // clobber the BFF's single stored authorization request (see `reauthInFlight`).
          return new Promise(() => {});
        }
        if (reauthOnCooldown()) {
          // We already came back from Keycloak and are STILL getting 401s — redirecting
          // again would loop. Let the error surface so the UI can show it.
          return Promise.reject(normalizeApiError(error));
        }

        // Persist the current SPA path so BffSessionProvider can restore it after re-auth.
        const returnPath = safeReturnPath(
          window.location.pathname + window.location.search + window.location.hash,
        );
        if (returnPath) {
          rememberInSession(REAUTH_RETURN_KEY, returnPath);
        }

        reauthInFlight = true;
        rememberInSession(REAUTH_ATTEMPT_KEY, String(Date.now()));
        window.location.assign(REAUTH_REDIRECT);
        // Return a never-resolving promise — navigation is already in progress.
        return new Promise(() => {});
      }
    }

    // The active tenant became inaccessible mid-session (e.g. membership revoked by an
    // admin while the user was still working in it). Notify the registered handler —
    // BffSessionProvider refreshes the session and shows a one-time notice — but this is
    // detection/dispatch only: the ORIGINAL request still fails normally below, exactly
    // like any other error, so its caller renders its own error state as usual. A plain
    // 403 (regular FORBIDDEN, no/other `code`) is left untouched.
    if (
      axios.isAxiosError(error) &&
      error.response?.status === 403 &&
      isTenantAccessDeniedBody(error.response.data)
    ) {
      tenantAccessDeniedHandler?.();
    }

    // ── Step-up (re-authentication) challenge ─────────────────────────────────
    // The caller HOLDS the permission but has not proved who they are recently enough to exercise a
    // capability classed SECRET / FINANCIAL / GOVERNANCE (adr-auth-004, plan-auth-001 Phase 4.4). Unlike
    // TENANT_ACCESS_DENIED above — which only notifies and lets the original request fail — this branch
    // ACTS: it parks the interrupted action, then re-initiates authorization with the `acr_values` the
    // error names. `StepUpReplay` finishes the job on the way back in.
    //
    // 403, not 401, and that is the point: the 401 path above asks Keycloak for the same assurance level
    // the BFF has just rejected, so routing this through it would loop forever without ever raising `acr`.
    if (axios.isAxiosError(error) && error.response?.status === 403 && error.config) {
      const challenge = parseStepUpChallenge(error.response.data);
      if (challenge) {
        if (reauthInFlight) {
          // A sibling challenge (or a 401) already started the navigation. A second start would clobber
          // the BFF's single stored authorization request — see `reauthInFlight`.
          return new Promise(() => {});
        }
        if (stepUpOnCooldown()) {
          // We came back from Keycloak moments ago and the assurance level STILL is not enough.
          // Redirecting again is the loop; say so instead, and let the call site render its error.
          stepUpUnsatisfiedHandler?.(challenge);
          return Promise.reject(normalizeApiError(error));
        }

        // Park what the user was doing, and where they were doing it. The return path rides the SAME
        // sessionStorage key the 401 path uses, so `BffSessionProvider` restores the screen with the
        // mechanism that already exists rather than a second one that has to be kept in step with it.
        const returnPath = safeReturnPath(currentSpaPath());
        writePendingStepUp(describePendingStepUp(error.config, challenge, returnPath));
        if (returnPath) {
          rememberInSession(REAUTH_RETURN_KEY, returnPath);
        }

        markStepUpStarted();
        reauthInFlight = true;
        window.location.assign(stepUpAuthorizeUrl(REAUTH_REDIRECT, challenge.requiredAcr));
        // Never-resolving: navigation is already in progress, and the caller's error UI would only
        // flash before the document is replaced.
        return new Promise(() => {});
      }
    }

    return Promise.reject(normalizeApiError(error));
  },
);

export { REAUTH_RETURN_KEY, REAUTH_ATTEMPT_KEY, REAUTH_REDIRECT };

/**
 * Clears the re-auth loop-breaker. Called by the session provider once `GET /api/v1/me`
 * succeeds: the round-trip worked, so the next expiry deserves its own transparent re-auth
 * rather than being suppressed by this one's leftover cooldown.
 */
export function clearReauthCooldown(): void {
  try {
    sessionStorage.removeItem(REAUTH_ATTEMPT_KEY);
  } catch {
    // Storage blocked — nothing was stored either, so there is nothing to clear.
  }
}
